import { Link } from "react-router-dom";
import { ShieldCheck, Check, X, Mail, ArrowLeft } from "lucide-react";

const TOC = [
  { id: "introduction", label: "Introduction" },
  { id: "information-we-collect", label: "Information We Collect" },
  { id: "how-we-use", label: "How We Use Information" },
  { id: "data-storage", label: "Data Storage" },
  { id: "data-sharing", label: "Data Sharing" },
  { id: "your-rights", label: "Your Rights & Controls" },
  { id: "third-party", label: "Third-Party Platforms" },
  { id: "children", label: "Children's Privacy" },
  { id: "changes", label: "Changes to This Policy" },
  { id: "contact", label: "Contact Us" },
];

const SectionHead = ({ n, children }) => (
  <div className="flex items-center gap-3 mb-3">
    <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-600 to-violet-700 text-white flex items-center justify-center text-xs font-extrabold shrink-0 shadow-sm shadow-purple-600/30">
      {n}
    </span>
    <h2 className="font-heading text-lg sm:text-xl font-extrabold text-slate-900">{children}</h2>
  </div>
);

export const PrivacyPolicyPage = () => {
  return (
    <div className="font-body text-slate-900 antialiased" data-testid="privacy-policy-page">
      {/* Top bar */}
      <div className="sticky top-0 z-50 flex items-center justify-between px-4 sm:px-8 py-4 border-b border-purple-100 bg-white/85 backdrop-blur-xl">
        <Link to="/" className="flex items-center gap-2.5" data-testid="privacy-logo-link">
          <span className="w-8 h-8 rounded-xl bg-gradient-to-br from-purple-600 to-violet-700 flex items-center justify-center shadow-sm shadow-purple-600/30">
            <ShieldCheck className="w-4 h-4 text-white" />
          </span>
          <span className="font-heading font-extrabold text-base text-slate-900">EasyLister</span>
        </Link>
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-purple-700 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to site
        </Link>
      </div>

      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-br from-purple-700 via-violet-700 to-fuchsia-700 px-6 py-16 sm:py-20 text-center text-white">
        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-fuchsia-400/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-purple-400/30 blur-3xl" />
        <span className="relative inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/15 border border-white/25 text-[11px] font-bold uppercase tracking-wider mb-5">
          Privacy Policy
        </span>
        <h1 className="relative font-heading text-3xl sm:text-4xl font-extrabold max-w-xl mx-auto leading-tight">
          Your data, handled simply and transparently
        </h1>
        <p className="relative mt-3 text-sm text-purple-100 max-w-md mx-auto">
          This page explains exactly what the EasyLister Chrome extension collects, what it doesn't, and how it's
          used. Effective date: <strong className="text-white">June 22, 2026</strong>.
        </p>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Table of contents */}
        <nav
          aria-label="Table of contents"
          className="rounded-3xl border border-purple-100 bg-purple-50/40 px-6 py-5 mb-10"
        >
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-purple-700 mb-3">On this page</div>
          <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {TOC.map((item, i) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  data-testid={`privacy-toc-${item.id}`}
                  className="flex items-baseline gap-2 text-sm font-semibold text-slate-700 hover:text-purple-700 transition-colors"
                >
                  <span className="text-purple-600 font-extrabold text-xs">{String(i + 1).padStart(2, "0")}</span>
                  {item.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <section id="introduction" className="pb-9 mb-9 border-b border-slate-100" data-testid="section-introduction">
          <SectionHead n="01">Introduction</SectionHead>
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            EasyLister is a Chrome browser extension that helps e-commerce sellers work faster on platforms like
            Meesho, Flipkart, and Amazon. It scans product listings, auto-fills seller forms with that scanned data,
            clones product details, and lets you download product images — all to cut down the repetitive manual
            work of listing products.
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            This policy explains, in plain language, what information EasyLister interacts with while doing that,
            how it's used, and the controls you have over it. We've kept the legal language to a minimum — if
            anything here is unclear, reach out using the contact details at the bottom of this page.
          </p>
        </section>

        <section id="information-we-collect" className="pb-9 mb-9 border-b border-slate-100" data-testid="section-information-we-collect">
          <SectionHead n="02">Information We Collect</SectionHead>
          <p className="text-sm text-slate-600 leading-relaxed mb-4">
            EasyLister only interacts with two narrow categories of information:
          </p>
          <ul className="space-y-2.5 mb-6">
            {[
              ["Website content from pages you actively visit", "product titles, prices, descriptions, images, categories, specifications, and colors, scanned only from seller platform pages (Meesho, Flipkart, Amazon) when you choose to scan or clone a listing."],
              ["Your activity inside the extension popup", "limited to which buttons you click (Scan, Clone, Autofill, Inject, Download Images, Clear Product), so the extension knows what action to perform."],
            ].map(([title, desc]) => (
              <li key={title} className="flex gap-2.5 text-sm text-slate-600 leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2 shrink-0" />
                <span>
                  <strong className="text-slate-800">{title}</strong> — {desc}
                </span>
              </li>
            ))}
          </ul>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-rose-100 bg-rose-50/50 p-5">
              <div className="text-[11px] font-extrabold uppercase tracking-wider text-rose-700 mb-3">We do NOT collect</div>
              <ul className="space-y-2">
                {[
                  "Personally identifiable information (name, email, address, age)",
                  "Health information",
                  "Financial or payment information",
                  "Authentication credentials or passwords",
                  "Personal communications",
                  "Location or GPS data",
                  "General web browsing history",
                  "Keystrokes or mouse movement outside the popup",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-[13px] text-slate-600 leading-snug">
                    <X className="w-3.5 h-3.5 text-rose-500 mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-purple-100 bg-purple-50/50 p-5">
              <div className="text-[11px] font-extrabold uppercase tracking-wider text-purple-700 mb-3">We DO scan, on request</div>
              <ul className="space-y-2">
                {["Product titles & descriptions", "Prices & categories", "Product images", "Specifications & colors", "Which extension button you clicked"].map((t) => (
                  <li key={t} className="flex items-start gap-2 text-[13px] text-slate-600 leading-snug">
                    <Check className="w-3.5 h-3.5 text-purple-600 mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section id="how-we-use" className="pb-9 mb-9 border-b border-slate-100" data-testid="section-how-we-use">
          <SectionHead n="03">How We Use Information</SectionHead>
          <p className="text-sm text-slate-600 leading-relaxed">
            Scanned product data is used for exactly one purpose: to auto-fill seller listing forms on e-commerce
            platforms, when you ask EasyLister to do so. Nothing is scanned in the background, and nothing is
            collected for advertising, analytics, or any purpose beyond completing the action you triggered.
          </p>
        </section>

        <section id="data-storage" className="pb-9 mb-9 border-b border-slate-100" data-testid="section-data-storage">
          <SectionHead n="04">Data Storage</SectionHead>
          <p className="text-sm text-slate-600 leading-relaxed">
            All scanned product data is stored <strong className="text-slate-800">locally on your device</strong>,
            using Chrome's built-in <code className="px-1.5 py-0.5 rounded bg-slate-100 text-[13px]">chrome.storage</code> API.
            No scanned product data is uploaded to, processed by, or sent to any external server or third-party
            service.
          </p>
        </section>

        <section id="data-sharing" className="pb-9 mb-9 border-b border-slate-100" data-testid="section-data-sharing">
          <SectionHead n="05">Data Sharing</SectionHead>
          <p className="text-sm text-slate-600 leading-relaxed">
            We do not sell, rent, transfer, or share user data with any third party, for any purpose. Your scanned
            product data stays on your device unless and until you act on it (for example, by clicking Autofill to
            place it into a form you're already viewing).
          </p>
        </section>

        <section id="your-rights" className="pb-9 mb-9 border-b border-slate-100" data-testid="section-your-rights">
          <SectionHead n="06">Your Rights &amp; Controls</SectionHead>
          <p className="text-sm text-slate-600 leading-relaxed mb-3">You're always in control of what EasyLister stores:</p>
          <ul className="space-y-2.5">
            {[
              "Click Clear Product inside the extension at any time to immediately delete stored product data.",
              "Uninstalling the extension removes all locally stored data along with it — nothing persists elsewhere.",
              "EasyLister only acts when you click a button — there's no background scanning or silent data collection to opt out of.",
            ].map((t) => (
              <li key={t} className="flex gap-2.5 text-sm text-slate-600 leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mt-2 shrink-0" /> {t}
              </li>
            ))}
          </ul>
        </section>

        <section id="third-party" className="pb-9 mb-9 border-b border-slate-100" data-testid="section-third-party">
          <SectionHead n="07">Third-Party Platforms</SectionHead>
          <p className="text-sm text-slate-600 leading-relaxed">
            EasyLister accesses pages on Meesho, Flipkart, and Amazon only when you are actively on those pages and
            initiate a scan or clone yourself. No background tracking, polling, or monitoring of these sites occurs.
            EasyLister is an independent tool and is not affiliated with, endorsed by, or operated by Meesho,
            Flipkart, or Amazon.
          </p>
        </section>

        <section id="children" className="pb-9 mb-9 border-b border-slate-100" data-testid="section-children">
          <SectionHead n="08">Children's Privacy</SectionHead>
          <p className="text-sm text-slate-600 leading-relaxed">
            EasyLister is built for e-commerce sellers and is not directed at children under 13. We do not knowingly
            collect any information from minors.
          </p>
        </section>

        <section id="changes" className="pb-9 mb-9 border-b border-slate-100" data-testid="section-changes">
          <SectionHead n="09">Changes to This Policy</SectionHead>
          <p className="text-sm text-slate-600 leading-relaxed mb-3">
            If this privacy policy changes, the updated version will be published on this page with a revised
            effective date. Continuing to use EasyLister after changes are posted means you accept the updated
            policy.
          </p>
          <div className="rounded-2xl border border-purple-100 border-l-4 border-l-purple-600 bg-purple-50/50 px-5 py-3.5 text-sm text-slate-700">
            <strong className="text-slate-900">Current effective date:</strong> June 22, 2026
          </div>
        </section>

        <section
          id="contact"
          className="rounded-3xl bg-gradient-to-br from-purple-700 via-violet-700 to-fuchsia-700 text-white text-center px-6 sm:px-10 py-10"
          data-testid="section-contact"
        >
          <div className="flex items-center justify-center gap-3 mb-3">
            <span className="w-8 h-8 rounded-xl bg-white/15 flex items-center justify-center text-xs font-extrabold shrink-0">10</span>
            <h2 className="font-heading text-lg sm:text-xl font-extrabold">Contact Us</h2>
          </div>
          <p className="text-sm text-purple-100 mb-5">Questions about this policy or how EasyLister handles data? We're happy to help.</p>
          <a
            href="mailto:support@easylister.co.in"
            data-testid="privacy-contact-email"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-white/15 border border-white/30 hover:bg-white/25 font-semibold text-sm transition-colors"
          >
            <Mail className="w-4 h-4" /> support@easylister.co.in
          </a>
        </section>
      </div>

      <footer className="text-center py-8 text-xs text-slate-400">© 2026 EasyLister. All rights reserved.</footer>
    </div>
  );
};
